/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon } from '@components/atoms'
import { MainNavTabs } from '@components/molecules/tab/MainNavTabs'
import { mono, boSize, boAdmin, boFont } from '../backoffice/backoffice.tokens'
import { useBoShell } from '../backoffice/BoShellContext'
import { menuTitle } from '../backoffice/menuTitle'
import { useLocation, matchPath } from 'react-router-dom'
import config from '@configs'

import { useAppSelector, useAppDispatch, configActions, TabType } from '@redux/index'

interface PropsType {
	style?: React.CSSProperties
	/** 쉘이 계산한 사이드바 폭 — 폰 0 · 태블릿 64 · 데스크톱 240/64 */
	sideWidth?: number
}

/**
 * 상단 탭 바. 높이 48, 페이지 그라운드 위에 하단 헤어라인 하나.
 * 관리자 모드에서는 파란 띠로 사이드바 맨 위의 관리자 모드 칸(DrawerHeader `.adminBand`)과 이어져 한 띠가 된다(DESIGN_SYSTEM §13.4).
 *
 * 폰(RESPONSIVE_PLAN §4.1): 열린 페이지 탭 대신 **≡ · 현재 화면 제목 · (관리자 모드 ×)** 한 줄.
 * 태블릿: 좌측 ≡ 가 임시 드로어를 열고 탭은 그대로.
 */
export const MainNav = (props: PropsType): JSX.Element => {
	// redux state
	const mode: 'main' | 'admin' = useAppSelector(state => state.config.mode)
	const drawerWidth: number = useAppSelector(state => state.config.drawerWidth)
	const closedDrawerWidth: number = useAppSelector(
		state => state.config.closedDrawerWidth,
	)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)
	const selectedTabIdx: string = useAppSelector(state => state.config.selectedTabIdx)
	const tabList: TabType[] = useAppSelector(state => state.config.tabList)
	const language: string = useAppSelector(state => state.user.language)
	const reduxDispatch = useAppDispatch()

	const shell = useBoShell()
	const isPhone = shell.sizeClass === 'phone'
	const isTablet = shell.sizeClass === 'tablet'

	const sideWidth: number =
		props.sideWidth ?? (isOpenDrawer ? drawerWidth : closedDrawerWidth)

	const location = useLocation()
	const currentTab: TabType | undefined = tabList[parseInt(selectedTabIdx) - 1]
	// 하위 페이지(sidePageList, `/main/settlement/:month` 등)는 탭이 없다 — 폰 상단 바 제목은 부모 탭(없으면 하위 페이지 이름)
	const sidePage = config
		.sidePage()
		.find(aPage => matchPath(aPage.to ?? '', location.pathname) !== null)
	const parentTab: TabType | undefined =
		sidePage !== undefined
			? tabList.find(aTab => aTab.path === sidePage.parentPath)
			: undefined
	const title =
		parentTab !== undefined
			? menuTitle(parentTab, language)
			: sidePage !== undefined
			? sidePage.name
			: currentTab
			? menuTitle(currentTab, language)
			: ''

	const menuButton =
		isPhone || isTablet ? (
			<Pressable
				className="boNavMenuBtn"
				onClick={() => shell.setMobileDrawerOpen(true)}
				aria-label="메뉴 열기"
			>
				<Icon name="menu" className="boNavMenuIcon" />
			</Pressable>
		) : null

	return (
		<View
			css={[
				style,
				{ left: sideWidth, width: `calc(100% - ${sideWidth}px)` },
				{ ...props.style },
			]}
			role="navigation"
			aria-label="열린 화면"
			className={`${mode === 'admin' ? 'admin' : ''} ${shell.sizeClass}`}
		>
			<View className="navInner">
				{menuButton}
				{isPhone ? (
					<>
						<span className="boNavTitle">{title}</span>
						{mode === 'admin' ? (
							<Pressable
								className="boNavAdminExit"
								onClick={() => reduxDispatch(configActions.MODE('main'))}
								aria-label="관리자 모드 나가기"
							>
								<span className="boNavAdminLabel">관리자 모드</span>
								<Icon name="close" className="boNavAdminIcon" />
							</Pressable>
						) : null}
					</>
				) : (
					<MainNavTabs />
				)}
			</View>
		</View>
	)
}

const style = css({
	position: 'fixed',
	top: 0,
	zIndex: 5,
	height: boSize.navH,
	background: mono.bg,
	borderBottom: `1px solid ${mono.line}`,
	boxSizing: 'border-box',
	fontFamily: boFont.ui,
	transition:
		'left 200ms ease-out, width 200ms ease-out, background-color 200ms ease-out',

	'.navInner': {
		display: 'flex',
		alignItems: 'stretch',
		height: '100%',
	},

	'.boNavMenuBtn': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 48,
		height: '100%',
		flexShrink: 0,
		'.boNavMenuIcon': { fontSize: 24, color: mono.ink },
	},

	'.boNavTitle': {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		alignItems: 'center',
		fontSize: 16,
		fontWeight: 500,
		color: mono.ink,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

	'.boNavAdminExit': {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		height: '100%',
		padding: '0 12px',
		flexShrink: 0,
		'.boNavAdminLabel': { fontSize: 13, fontWeight: 700, color: boAdmin.textOn },
		'.boNavAdminIcon': { fontSize: 18, color: boAdmin.textOn },
	},

	'&.phone': {
		height: `calc(${boSize.navH}px + env(safe-area-inset-top, 0px))`,
		paddingTop: 'env(safe-area-inset-top, 0px)',
	},

	'&.admin': {
		background: boAdmin.bar,
		borderBottomColor: boAdmin.bar,
		'.boNavMenuBtn .boNavMenuIcon': { color: boAdmin.textOn },
		'.boNavTitle': { color: boAdmin.textOn },
	},
})
